import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";

// Hash → stabile HSL-Farbe (hell/dunkel gut lesbar)
function hashToHsl(str: string, s = 62, l = 38) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return `hsl(${h % 360} ${s}% ${l}%)`;
}

// Map: Initialen → Menge unterschiedlicher author_id
function buildCollisionMap(rows: ThreadRow[]) {
  const map = new Map<string, Set<string>>();
  rows.forEach((r) => {
    if (!map.has(r.author_initials)) map.set(r.author_initials, new Set());
    map.get(r.author_initials)!.add(r.author_id);
  });
  return map;
}


type ThreadRow = {
  id: string;
  title: string;
  body: string | null;
  status: "open" | "planned" | "in_progress" | "done" | "wont_fix";
  created_at: string;
  author_initials: string;
  author_id: string;   // <— NEU
  votes_count: number;
};


type SortKey = "top" | "new";
type StatusFilter = "all" | "open" | "planned" | "in_progress" | "done" | "wont_fix";

export default function FeedbackList() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortKey>("top");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const collisionMap = useMemo(() => buildCollisionMap(threads), [threads]);

  // Map: thread_id → true (hat der aktuelle User gevotet?)
  const [votedMap, setVotedMap] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    setError(null);

    // 1) Threads via RPC laden (inkl. votes_count, aber OHNE user_voted)
    const { data, error } = await supabase.rpc("feedback_list", {
      p_status: statusFilter,
      p_sort: sort,
    });

    if (error) {
      console.error("feedback_list failed:", error.message);
      setError("Konnte Feedback nicht laden.");
      setThreads([]);
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as ThreadRow[];
    setThreads(rows);

    // 2) Falls eingeloggt, eigene Votes für diese Threads nachziehen
    if (user && rows.length > 0) {
      const ids = rows.map((r) => r.id);
      const { data: myVotes, error: vErr } = await supabase
        .from("feedback_votes")
        .select("thread_id")
        .in("thread_id", ids);

      if (!vErr && myVotes) {
        const map: Record<string, boolean> = {};
        myVotes.forEach((v) => (map[v.thread_id] = true));
        setVotedMap(map);
      } else {
        setVotedMap({});
      }
    } else {
      setVotedMap({});
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, statusFilter, user?.id]);

  const disableVoteHint = useMemo(
    () => (!user ? "Bitte einloggen, um abzustimmen." : ""),
    [user]
  );

  const toggleVote = async (threadId: string) => {
    if (!user) {
      alert("Bitte logge dich ein, um abzustimmen.");
      return;
    }
    // Optimistic UI: lokalen Count & voted-Flag toggeln
    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId
          ? {
              ...t,
              votes_count: (votedMap[threadId] ? t.votes_count - 1 : t.votes_count + 1),
            }
          : t
      )
    );
    setVotedMap((prev) => ({ ...prev, [threadId]: !prev[threadId] }));

    const { error } = await supabase.rpc("feedback_toggle_vote", {
      p_thread_id: threadId,
    });
    if (error) {
      console.error("toggle_vote failed:", error.message);
      // Rollback
      setThreads((prev) =>
        prev.map((t) =>
          t.id === threadId
            ? {
                ...t,
                votes_count: (!votedMap[threadId] ? t.votes_count - 1 : t.votes_count + 1),
              }
            : t
        )
      );
      setVotedMap((prev) => ({ ...prev, [threadId]: !prev[threadId] }));
      alert("Konnte deine Stimme nicht speichern.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-20 pb-20 px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h1 className="text-2xl font-bold text-slate-100">💡 Feedback & Ideen</h1>

          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="border border-[#2a3347] rounded-lg px-3 py-2 text-sm bg-[#0d1117] text-slate-200"
              title="Status filtern"
            >
              <option value="all">Alle</option>
              <option value="open">Offen</option>
              <option value="planned">Geplant</option>
              <option value="in_progress">In Arbeit</option>
              <option value="done">Erledigt</option>
              <option value="wont_fix">Kein Fix</option>
            </select>

            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="border border-[#2a3347] rounded-lg px-3 py-2 text-sm bg-[#0d1117] text-slate-200"
              title="Sortierung"
            >
              <option value="top">Top</option>
              <option value="new">Neu</option>
            </select>

            <button
              onClick={() => setNewOpen(true)}
              className="bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition"
            >
              + Neues Feedback
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-slate-400">⏳ Lade Feedback…</p>
        ) : error ? (
          <p className="text-red-400">{error}</p>
        ) : threads.length === 0 ? (
          <p className="text-slate-400">Noch keine Einträge.</p>
        ) : (
          <ul className="space-y-3">
            {threads.map((t) => {
              const voted = !!votedMap[t.id];
              return (
                <li
                  key={t.id}
                  className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 flex items-start gap-4 hover:shadow-sm transition"
                >
                  {/* Vote & Count */}
                  <button
                    className={`min-w-16 flex flex-col items-center justify-center rounded-lg border px-3 py-2 ${
                      voted ? "bg-sky-900/40 border-sky-400" : "bg-[#1e2639] border-[#2a3347]"
                    } ${!user ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                    onClick={() => (user ? toggleVote(t.id) : undefined)}
                    title={disableVoteHint || (voted ? "Abstimmung zurücknehmen" : "Ich will das!")}
                  >
                    <span className="text-lg">{voted ? "👍" : "👍"}</span>
                    <span className="text-sm font-semibold tabular-nums text-slate-200">{t.votes_count}</span>
                  </button>

                  {/* Inhalt */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={t.status} />
                      <span className="text-xs text-slate-500">
                        {new Date(t.created_at).toLocaleDateString()}
                      </span>
                      <span className="text-xs text-slate-500">•</span>
                      <span
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs"
                      style={{
                          backgroundColor:
                          (collisionMap.get(t.author_initials)?.size ?? 0) > 1
                              ? hashToHsl(t.author_id)  // mehrere User mit denselben Initialen → farbig
                              : "#334155",              // sonst slate-700
                      }}
                      title={
                          (collisionMap.get(t.author_initials)?.size ?? 0) > 1
                          ? `Mehrere Autoren mit ${t.author_initials}`
                          : `Autor ${t.author_initials}`
                      }
                      >
                      {t.author_initials}
                      </span>

                    </div>

                    <h2 className="text-lg font-semibold text-slate-100">{t.title}</h2>
                    {t.body && (
                      <p className="text-sm text-slate-400 mt-1 line-clamp-3">{t.body}</p>
                    )}

                    <div className="mt-2">
                      <a
                        href={`/feedback/${t.id}`}
                        className="text-sky-400 hover:underline text-sm"
                      >
                        Details & Diskussion
                      </a>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {newOpen && (
          <FeedbackNewModal
            onClose={() => setNewOpen(false)}
            onCreated={() => {
              setNewOpen(false);
              load();
            }}
          />
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ThreadRow["status"] }) {
  const map: Record<ThreadRow["status"], string> = {
    open: "bg-slate-700/30 text-slate-300 border-slate-600",
    planned: "bg-sky-900/30 text-sky-400 border-sky-800",
    in_progress: "bg-amber-900/30 text-amber-400 border-amber-800",
    done: "bg-emerald-900/30 text-emerald-400 border-emerald-800",
    wont_fix: "bg-red-900/30 text-red-400 border-red-800",
  };
  const label: Record<ThreadRow["status"], string> = {
    open: "Offen",
    planned: "Geplant",
    in_progress: "In Arbeit",
    done: "Erledigt",
    wont_fix: "Kein Fix",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded border ${map[status]}`}>{label[status]}</span>
  );
}

function FeedbackNewModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const disabled =
    title.trim().length < 5 ||
    title.trim().length > 120 ||
    body.length > 2000 ||
    sending;

  const submit = async () => {
    setSending(true);
    setError(null);

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setError("Bitte logge dich ein, um Feedback zu erstellen.");
      setSending(false);
      return;
    }

    const { error } = await supabase.from("feedback_threads").insert({
      user_id: user.id,
      title: title.trim(),
      body: body.trim() || null,
      // status bleibt 'open' (Default)
      // author_initials wird per Trigger gesetzt
    });

    if (error) {
      console.error("insert feedback_thread failed:", error.message);
      setError("Konnte dein Feedback nicht speichern.");
      setSending(false);
      return;
    }

    onCreated();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-[#161b27] border border-[#2a3347] w-full max-w-xl rounded-2xl p-6 shadow-xl">
        <h3 className="text-lg font-semibold mb-2 text-slate-100">Neues Feedback</h3>
        <p className="text-xs text-slate-500 mb-4">
          Beschreibe kurz dein Anliegen. <strong className="text-slate-400">Bitte keine persönlichen Daten hier eingeben, da alle anderen User hier dein Feedback und Kommentare sehen können!</strong>
        </p>

        {error && <p className="text-sm text-red-400 mb-2">{error}</p>}

        <label className="block text-sm font-medium text-slate-200 mb-1">Titel</label>
        <input
          className="w-full border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg px-3 py-2 mb-3 outline-none focus:border-sky-400"
          placeholder="Kurz und prägnant…"
          value={title}
          maxLength={120}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label className="block text-sm font-medium text-slate-200 mb-1">Beschreibung (optional)</label>
        <textarea
          className="w-full border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg px-3 py-2 min-h-[120px] outline-none focus:border-sky-400"
          placeholder="Optional: Details, Schritte zum Reproduzieren, Vorschläge…"
          value={body}
          maxLength={2000}
          onChange={(e) => setBody(e.target.value)}
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            className="px-3 py-2 rounded-lg border border-[#2a3347] text-slate-300 bg-[#1e2639] hover:bg-[#2a3347]"
            onClick={onClose}
            disabled={sending}
          >
            Abbrechen
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              disabled ? "bg-[#2a3347] text-slate-500" : "bg-sky-500 text-white hover:bg-sky-600"
            }`}
            onClick={submit}
            disabled={disabled}
          >
            {sending ? "Speichere…" : "Erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}
